import { useState } from "react";
import { validateField } from "../../../services/Utils";

/**
 * @typedef {Object} FieldConfig
 * @property {string} label - The field label (for error messages)
 * @property {boolean} [required] - Whether the field is required
 * @property {RegExp} [regex] - Regex for validation
 * @property {number} [minLength] - Minimum length
 * @property {number} [maxLength] - Maximum length
 * @property {Object} [customMessages] - Custom error messages
 * @property {string} [customMessages.regex] - Custom regex error message
 * @property {string} [customMessages.minLength] - Custom minLength error message
 * @property {string} [customMessages.maxLength] - Custom maxLength error message
 */

/**
 * @typedef {Object.<string, FieldConfig>} FieldsConfig
 */

/**
 * Custom hook for form validation
 * @param {FieldsConfig} fieldsConfig - Configuration for form fields
 * @returns {Object} Form validation state and handlers
 */
export default function useFormValidation(fieldsConfig) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    value = value.replace(" ", "");
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: [] }));
  };

  const validate = () => {
    let valid = true;
    let newErrors = {};
    for (const field in fieldsConfig) {
      const config = fieldsConfig[field];
      const { isValid, messages } = validateField({
        name: config.label,
        value: values[field] || "",
        required: config.required,
        minLength: config.minLength,
        maxLength: config.maxLength,
        regex: config.regex,
        customMessages: config.customMessages,
      });
      if (!isValid) {
        valid = false;
        newErrors[field] = messages;
      }
    }
    setErrors(newErrors);
    return valid;
  };

  return {
    values,
    errors,
    handleChange,
    validate,
    setValues,
    setErrors,
  };
}
